/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useCallback, useMemo, useState } from 'react';
import { Link } from '@/components/link';
import type { ShopperSearch } from '@/scapi';
import { createProductUrl, getImagesForColor } from '@/lib/product/product-utils';
import { useDynamicImageContext } from '@/providers/dynamic-image';
import { ProductImage } from './product-image';
import ImageNavArrows from '@/components/image-nav-arrows';
import { TileImageCarousel } from '@/components/tile-image-carousel';
import { useTranslation } from 'react-i18next';
import { resolveImageUrl } from '@/lib/images/resolve-image-url';
import type { ImageContext } from '@/lib/images/resolve-image-url';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import type { AppConfig } from '@/types/config';

interface ProductImageContainerProps {
    product: ShopperSearch.schemas['ProductSearchHit'];
    selectedColorValue?: string | null;
    className?: string;
    handleProductClick?: (product: ShopperSearch.schemas['ProductSearchHit']) => void;
    /** Image aspect ratio (width/height). If provided, calculates height based on viewport width. Defaults to 1 (square) */
    imgAspectRatio?: number;
    /** Show prev/next navigation arrows when multiple images are available */
    showNavigationArrows?: boolean;
    /** Render a scrollable image carousel instead of a single image when multiple images are available */
    showCarousel?: boolean;
    /** Cloudinary image context — determines transformation dimensions */
    context?: ImageContext;
}

const ProductImageContainer = ({
    product,
    selectedColorValue = null,
    handleProductClick,
    imgAspectRatio = 1,
    showNavigationArrows = false,
    showCarousel = false,
    context,
}: ProductImageContainerProps) => {
    const { t } = useTranslation('product');
    const [selectedImageIndex, setSelectedImageIndex] = useState(0);

    // Get all images for the selected color variant
    const allImages = useMemo(
        () => getImagesForColor(product, selectedColorValue, 'hi-res'),
        [product, selectedColorValue]
    );

    const imageContext = useDynamicImageContext();
    const config = useConfig<AppConfig>();
    const carouselKey = useMemo(
        () =>
            [
                product.productId,
                selectedColorValue,
                ...allImages.map((image) => image.disBaseLink || image.link || ''),
            ].join('|'),
        [allImages, product.productId, selectedColorValue]
    );

    const handleClick = useCallback(() => {
        handleProductClick?.(product);
    }, [handleProductClick, product]);

    const imageAltFallback = product.productName || t('imageAlt') || 'Product Image';
    const productUrl = createProductUrl(product.productId, selectedColorValue);

    // When carousel mode is enabled and there are multiple images, render the carousel.
    if (showCarousel && allImages.length > 1) {
        return (
            <TileImageCarousel
                key={carouselKey}
                images={allImages}
                productUrl={productUrl}
                productName={imageAltFallback}
                widths={imageContext?.widths}
                imgAspectRatio={imgAspectRatio}
                handleProductClick={handleClick}
                context={context}
                className="w-full [&_img]:object-cover! [&_img]:h-full! [&_img]:max-w-full! [&_img]:mx-auto!"
            />
        );
    }

    const currentImage = allImages[selectedImageIndex] ?? allImages[0] ?? product.image;
    const rawImageUrl = currentImage?.disBaseLink || currentImage?.link;
    const currentImageUrl = rawImageUrl ? resolveImageUrl({ src: rawImageUrl, config, context }) : undefined;
    currentImageUrl && imageContext?.addSource(currentImageUrl);

    // When a non-square aspect ratio is requested, apply it via the native CSS
    // `aspect-ratio` property. The legacy padding-bottom percentage trick is not
    // used here because it conflicts with the native property and collapses the
    // image height to zero.
    const heightStyle = { aspectRatio: String(imgAspectRatio) };

    return (
        <div
            className={`${showNavigationArrows ? 'group/image ' : ''}relative overflow-hidden bg-secondary/20 flex flex-col [&_img]:w-full`}
            style={heightStyle}>
            {/* Product Image */}
            <Link
                to={productUrl}
                onClick={handleClick}
                className="block w-full h-full"
                aria-label={t('viewProductAriaLabel', { productName: imageAltFallback }) || imageAltFallback}>
                <ProductImage
                    src={currentImageUrl || ''}
                    alt={currentImage?.alt || imageAltFallback}
                    className="w-full h-full"
                    widths={imageContext?.widths}
                    imageProps={{
                        className: 'w-full',
                    }}
                />
            </Link>
            {/* Navigation Arrows - visible on hover */}
            {showNavigationArrows && allImages.length > 1 && (
                <ImageNavArrows
                    imageCount={allImages.length}
                    onIndexChange={setSelectedImageIndex}
                    className="opacity-0 group-hover/image:opacity-100 transition-opacity"
                />
            )}
        </div>
    );
};

export { ProductImageContainer };
